import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { isReservedUsername, type RouteStatus } from '@xingroute/schema'
import { enumDisplay } from '../lib/enumDisplay.js'
import { formatRelative } from '../lib/relativeTime.js'
import { FavoriteHeartButton } from './FavoriteHeartButton.js'
import { RouteThumbMap } from './RouteThumbMap.js'
import { Badge } from './ui/Misc.js'

export interface RouteCardRoute {
  id: string
  slug: string
  title: string
  summary?: string | null
  activities: string[]
  status?: RouteStatus
  distance_km?: number | null
  ascent_m?: number | null
  cover_image_url?: string | null
  geometry_url?: string | null
  thumbnail_points?: Array<{ lat: number; lon: number; ele?: number; time?: string }>
  thumbnail_draw_line?: boolean
  hasGeometry?: boolean
  created_at?: string | null
  updated_at?: string | null
  creator_username?: string | null
  creator_name?: string | null
  favorite_count?: number | null
}

export interface RouteCardProps {
  route: RouteCardRoute
  favoriteInitial?: boolean
  showFavoriteHeart?: boolean
  showStatusChip?: boolean
  /**
   * Controls whether the card surfaces interactive affordances (favorite heart).
   * When false (typically for logged-out viewers who cannot meaningfully
   * favorite routes), only the link surface is rendered. Defaults to true.
   */
  interactive?: boolean
}

export function RouteCard({
  route,
  favoriteInitial = false,
  showFavoriteHeart = true,
  showStatusChip = false,
  interactive = true,
}: RouteCardProps) {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const meta = formatMeta(route, locale, t as TFunction)

  return (
    <li data-testid="route-card" className="group/route-card relative">
      <div className="relative overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] transition-colors duration-150 ease-out hover:border-[var(--xr-border-strong)] has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--xr-brand-primary)]">
        <Link
          to={`/r/${route.slug}`}
          className="group/route-link block min-w-0 focus-visible:outline-none"
          onKeyDown={(event) => {
            if (event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault()
              event.currentTarget.click()
            }
          }}
        >
          <div className="relative">
            <RouteThumbMap
              routeId={route.id}
              hasGeometry={route.hasGeometry ?? Boolean(route.geometry_url)}
              points={route.thumbnail_points}
              drawLine={route.thumbnail_draw_line}
              fallbackUrl={route.cover_image_url ?? undefined}
              status={route.status}
              className="aspect-[4/3] w-full overflow-hidden bg-[var(--xr-bg-surface-elev)] md:aspect-[2/1]"
            />
            <MapMetaChip route={route} locale={locale} t={t as TFunction} />
          </div>
          <div className="flex min-h-[130px] flex-col p-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <h2 className="truncate text-base font-semibold text-[var(--xr-text-primary)] group-hover/route-link:underline group-focus-visible/route-link:underline">
                {route.title}
              </h2>
              {showStatusChip && route.status && (
                <Badge tone={route.status === 'published' ? 'success' : 'neutral'}>
                  {enumDisplay('routeStatus', route.status, t as TFunction).label}
                </Badge>
              )}
            </div>
            {meta && <p className="mb-2 truncate text-xs text-[var(--xr-text-muted)]">{meta}</p>}
            <p className="mb-3 line-clamp-2 min-h-[2.6em] text-sm text-[var(--xr-text-secondary)]">
              {stripInlineMarkdown(route.summary) || '—'}
            </p>
            <div className="mt-auto flex flex-nowrap items-center gap-1 overflow-hidden">
              {route.activities.map((activity) => (
                <Badge key={activity} tone={activityTone(activity)}>
                  {enumDisplay('activity', activity, t as TFunction).label}
                </Badge>
              ))}
            </div>
          </div>
        </Link>
      </div>
      {interactive && showFavoriteHeart && (
        <div className="absolute right-3 top-3 z-10">
          <FavoriteHeartButton routeId={route.id} initial={favoriteInitial} />
        </div>
      )}
    </li>
  )
}

function activityTone(activity: string): 'brand' | 'accent' | 'neutral' {
  if (activity === 'hiking') return 'brand'
  if (activity === 'running') return 'accent'
  return 'neutral'
}

// RouteCard summaries come from `description.md` excerpts that may carry
// inline Markdown emphasis (`**bold**`, `*italic*`, `` `code` ``). The card
// renders plain text inside a line-clamped paragraph, so leaving the raw
// markers in place would surface visual noise like `**字段**`. Strip the
// emphasis markers without invoking a full Markdown renderer.
function stripInlineMarkdown(input: string | null | undefined): string {
  if (!input) return ''
  return input
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
    .replace(/_{2}([^_]+)_{2}/g, '$1')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1$2')
    .replace(/`([^`\n]+)`/g, '$1')
}

function MapMetaChip({
  route,
  locale,
  t,
}: {
  route: RouteCardRoute
  locale: string
  t: TFunction
}) {
  const distance = route.distance_km
  const elevation = route.ascent_m
  if (distance == null && elevation == null) return null

  let label: string
  if (distance != null && elevation != null) {
    label = t('routeCard.metaChip.distanceElevation', {
      distance: formatDistance(distance, locale),
      elevation: formatElevation(elevation, locale),
    })
  } else if (distance != null) {
    label = t('routeCard.metaChip.distanceOnly', {
      distance: formatDistance(distance, locale),
    })
  } else {
    label = t('routeCard.metaChip.elevationOnly', {
      elevation: formatElevation(elevation ?? 0, locale),
    })
  }

  return (
    <span className="absolute bottom-2 right-2 rounded-[var(--xr-radius-md)] bg-[var(--xr-overlay-bg)] px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">
      {label}
    </span>
  )
}

function formatMeta(route: RouteCardRoute, locale: string, t: TFunction): string {
  const whenSource = route.created_at ?? route.updated_at
  const favoriteCount = route.favorite_count ?? 0
  const parts: string[] = []
  const creatorName = safeCreatorName(route.creator_name, route.creator_username)
  if (creatorName) parts.push(creatorName)
  if (whenSource) parts.push(formatRelative(whenSource, locale))
  parts.push(t('routeCard.favoriteCount', { count: favoriteCount }))
  return parts.join(' · ')
}

function safeCreatorName(
  name: string | null | undefined,
  username: string | null | undefined,
): string {
  const publicName = name?.trim()
  if (publicName) return publicName
  const handle = username?.trim()
  return handle && !isReservedUsername(handle) ? handle : ''
}

function formatDistance(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
}

function formatElevation(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(value))
}
